import React, { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import NeuralBackground from "../../ui/flow-field-background";
import { Link } from "react-router-dom";

const Careers = () => {
  const qrRef = useRef(null);

  const handleDownloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;

    const image = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = image;
    link.download = "scanglobal-careers-qr.png";
    link.click();
  };
  return (
    <>
      <NeuralBackground
        color="#c5ae7a" // Indigo-400
        scale={1}
        trailOpacity={0.1} // Lower = longer trails
        speed={0.8}
      />

      {/* <!-- Hero Section --> */}
      <section className="flex min-h-[90vh] z-20 relative items-end">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* <!-- Left Column - Heading --> */}
            <div className="order-1 animate-slide-in-blur">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[0.9] font-semibold tracking-tighter">
                Join Our Team
              </h1>
              <div className="flex gap-3 animate-fade-in-slide text-xs text-white/70 mt-10 mb-6 items-center">
                <a
                  href="#openings"
                  className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 backdrop-blur border border-white/10"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span className="font-medium text-white/90">
                    3 Open Positions
                  </span>
                </a>
                <div className="hidden md:block w-px h-4 bg-white/20"></div>
                <span className="hidden md:inline font-normal">Hiring Now</span>
              </div>

              <p className="text-lg sm:text-xl leading-relaxed  text-white/80 mb-8">
                We're looking for talented individuals to join our growing team.
                Explore our current openings or pitch us your unique skill set.
              </p>

              <div className="border-t border-white/10 pt-6 mb-6 animate-fade-in-scale">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="relative inline-block group rounded-full">
                    <a
                      href="#openings"
                      className="glow-button z-10 overflow-hidden transition-transform duration-150 ease-out active:scale-[0.98] hover:bg-gold/90 text-black bg-gold border-gold border rounded-full px-6 py-3 relative shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] cursor-pointer"
                    >
                      <span className="inline-flex items-center gap-2 font-normal z-10 rounded-full relative">
                        View Openings
                        <svg
                          className="h-5 w-5 transition-transform duration-200 ease-out group-hover:translate-x-0.5 rounded-full"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 12h14M13 5l7 7-7 7"
                          ></path>
                        </svg>
                      </span>
                      <span className="pointer-events-none absolute bottom-0 left-1/2 right-1/2 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80 transition-[left,right] duration-500 ease-out group-hover:left-0 group-hover:right-0 rounded-full"></span>
                      <span
                        className="glow pointer-events-none absolute inset-0 -z-10 rounded-full"
                        aria-hidden="true"
                      ></span>
                    </a>
                    <span
                      className="pointer-events-none absolute -bottom-3 left-1/2 z-0 h-6 w-44 -translate-x-1/2 transition-opacity duration-300 ease-out group-hover:opacity-100 rounded-full glow-shadow"
                      aria-hidden="true"
                    ></span>
                  </div>

                  <div className="w-px h-6 bg-white/20"></div>

                  <a
                    href="#qrcode"
                    className="inline-flex gap-2 transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 text-base font-normal text-white/90 border-white/10 border rounded-full px-5 py-2.5 backdrop-blur items-center cursor-pointer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    Get QR Code
                  </a>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 ">
                <p className="text-white/70">
                  Scan our QR code at the event or apply directly from this
                  page.
                </p>
              </div>
            </div>

            {/* <!-- Right Column - Content --> */}
            <div className="order-2 lg:order-2">
              {/* <div className="flex gap-3 animate-fade-in-slide text-xs text-white/70 mb-6 items-center">
                <div className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 backdrop-blur border border-white/10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span className="font-medium text-white/90">
                    3 Open Positions
                  </span>
                </div>
                <div className="hidden md:block w-px h-4 bg-white/20"></div>
                <span className="hidden md:inline font-normal">Hiring Now</span>
              </div>

              <p className="text-lg sm:text-xl leading-relaxed  text-white/80 mb-8">
                We're looking for talented individuals to join our growing team.
                Explore our current openings or pitch us your unique skill set.
              </p>

              <div className="border-t border-white/10 pt-6 mb-6 animate-fade-in-scale">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="relative inline-block group rounded-full">
                    <button className="glow-button z-10 overflow-hidden transition-transform duration-150 ease-out active:scale-[0.98] hover:bg-white/15 text-white bg-white/10 border-white/20 border rounded-full px-6 py-3 relative shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] cursor-pointer">
                      <span className="inline-flex items-center gap-2 font-normal z-10 rounded-full relative">
                        View Openings
                        <svg
                          className="h-5 w-5 transition-transform duration-200 ease-out group-hover:translate-x-0.5 rounded-full"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 12h14M13 5l7 7-7 7"
                          ></path>
                        </svg>
                      </span>
                      <span className="pointer-events-none absolute bottom-0 left-1/2 right-1/2 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80 transition-[left,right] duration-500 ease-out group-hover:left-0 group-hover:right-0 rounded-full"></span>
                      <span
                        className="glow pointer-events-none absolute inset-0 -z-10 rounded-full"
                        aria-hidden="true"
                      ></span>
                    </button>
                    <span
                      className="pointer-events-none absolute -bottom-3 left-1/2 z-0 h-6 w-44 -translate-x-1/2 transition-opacity duration-300 ease-out group-hover:opacity-100 rounded-full glow-shadow"
                      aria-hidden="true"
                    ></span>
                  </div>

                  <div className="w-px h-6 bg-white/20"></div>

                  <button className="inline-flex gap-2 transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 text-base font-normal text-white/90 border-white/10 border rounded-full px-5 py-2.5 backdrop-blur items-center cursor-pointer">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    Get QR Code
                  </button>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 ">
                <p className="text-sm text-white/45">
                  Scan our QR code at the event or apply directly from this
                  page.
                </p>
              </div> */}
              <video
                src="/videos/video1.mp4"
                className="rounded-2xl order-3 lg:order-2 w-full h-auto object-cover shadow-xl"
                autoPlay
                loop
                muted
                playsInline
              />
            </div>
            {/* <img
              src="/images/profile/hiring.jpg"
              alt="img"
              className="rounded-2xl order-3 lg:order-2 w-full h-auto object-cover shadow-xl"
            /> */}
          </div>
        </div>
      </section>

      {/* <!-- Job Listings Section --> */}
      <section id="openings" className="z-20 border-white/10 border-t relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-16">
          {/* <!-- Header --> */}
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase  text-white/70 tracking-widest bg-white/5 border-white/10 border rounded-full px-3 py-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
              </svg>
              Open Positions
            </span>
            <h2 className="text-3xl sm:text-5xl  font-semibold tracking-tight mt-4">
              Current Opportunities
            </h2>
            <p className="leading-relaxed  text-white/70 mt-3">
              Explore our available roles or propose your own position.
            </p>
          </div>

          {/* <!-- Job Cards Grid --> */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  mt-12 gap-6">
            {/* <!-- Job Card 1: Sales Representative --> */}
            <div className="group hover:bg-white/10 hover:border-gold/20 transition-colors bg-white/5 border-white/10 border rounded-2xl p-6 backdrop-blur">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#c5ae7a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-2">
                Sales Representative
              </h3>
              <p className="text-sm text-white/60 mb-4">Real Estate Division</p>

              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm text-white/80">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c5ae7a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-0.5 flex-shrink-0"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  Proven sales track record
                </li>
                <li className="flex items-start gap-2 text-sm text-white/80">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c5ae7a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-0.5 flex-shrink-0"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  Real estate experience preferred
                </li>
                <li className="flex items-start gap-2 text-sm text-white/80">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c5ae7a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-0.5 flex-shrink-0"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  Strong communication skills
                </li>
              </ul>

              <a
                href="mailto:admin@scanglobal.me"
                className="w-full inline-flex items-center justify-center gap-2 hover:bg-gold hover:text-black transition text-sm font-medium text-gold/90  border-gold/50 border rounded-full px-4 py-2"
              >
                Apply Now
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </a>
            </div>

            {/* <!-- Job Card 2: Sales Manager --> */}
            <div className="group hover:bg-white/10 transition-colors bg-white/5 border-white/10 border rounded-2xl p-6 backdrop-blur">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#c5ae7a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-2">
                Sales Manager
              </h3>
              <p className="text-sm text-white/60 mb-4">Leadership Role</p>

              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2 text-sm text-white/80">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c5ae7a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-0.5 flex-shrink-0"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  3+ years management experience
                </li>
                <li className="flex items-start gap-2 text-sm text-white/80">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c5ae7a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-0.5 flex-shrink-0"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  Team building expertise
                </li>
                <li className="flex items-start gap-2 text-sm text-white/80">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c5ae7a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-0.5 flex-shrink-0"
                  >
                    <path d="M20 6 9 17l-5-5"></path>
                  </svg>
                  Strategic planning ability
                </li>
              </ul>

              <a
                href="mailto:admin@scanglobal.me"
                className="w-full inline-flex items-center justify-center gap-2 hover:bg-gold hover:text-black transition text-sm font-medium text-gold/90  border-gold/50 border rounded-full px-4 py-2"
              >
                Apply Now
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </a>
            </div>

            {/* <!-- Job Card 3: New Role (Special) --> */}
            <div className="group hover:bg-white/10 transition-colors bg-white/5 border-white/20 border-2 rounded-2xl p-6 backdrop-blur relative overflow-hidden new-role-card">
              <div className="absolute inset-0 glow-border pointer-events-none"></div>

              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gold/10 bg-white/5 mb-4 relative z-10">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#c5ae7a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-2 relative z-10">
                Create Your Role
              </h3>
              <p className="text-sm text-white/60 mb-4 relative z-10">
                Pitch Your Skills
              </p>

              <p className="text-sm text-white/70 leading-relaxed mb-6 relative z-10">
                If you believe you have a skill or a combination of skills in a
                new role, let us know your ideas and we will review and see if
                this aligns with our company's goals.
              </p>

              <a
                href="mailto:admin@scanglobal.me"
                className="w-full inline-flex items-center justify-center gap-2 hover:bg-gold hover:text-black transition text-sm font-medium text-gold/90  border-gold/50 border rounded-full px-4 py-2 relative z-10"
              >
                Submit Your Pitch
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </a>
            </div>
          </div>

          {/* <!-- CTA --> */}
          <div className="flex  mt-8 justify-center">
            <p className="text-base text-white/60">
              Questions? Email us at{" "}
              <a
                href="mailto:admin@scanglobal.me"
                className=" hover:!text-white underline !text-gold"
              >
                admin@scanglobal.me
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* <!-- QR Code Section --> */}
      <section id="qrcode" className="z-20 border-white/10 border-t relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-20">
          {/* <!-- Header --> */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase  text-white/70 tracking-widest bg-white/5 border-white/10 border rounded-full px-3 py-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              For Print Materials
            </span>
            <h2 className="text-3xl sm:text-5xl  font-semibold tracking-tight mt-4">
              QR Code for Event
            </h2>
            <p className="leading-relaxed  text-white/70 mt-3">
              Download and print this QR code for event signage. Links directly
              to this hiring page.
            </p>
          </div>

          {/* <!-- QR Code Display --> */}
          <div className="max-w-md mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur text-center ">
              {/* <!-- QR Code Container --> */}
              {/* QR Code Container */}
              <div
                ref={qrRef}
                className="bg-white rounded-2xl shadow-lg border-4 p-2  mb-6 inline-block"
              >
                <QRCodeCanvas
                  value="https://scanglobal.me/careers"
                  // value="https://scanglobal-website.web.app/careers"
                  size={250}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="H"
                  includeMargin={true}
                  imageSettings={{
                    src: "/images/color-logo.png",
                    x: undefined,
                    y: undefined,
                    height: 34,
                    width: 34,
                    excavate: true,
                  }}
                />
                <QRCodeCanvas
                  value="https://scanglobal.me/"
                  // value="https://scanglobal-website.web.app/careers"
                  size={250}
                  bgColor="#000000"
                  fgColor="#c5ae7a"
                  level="H"
                  includeMargin={true}
                  imageSettings={{
                    src: "/images/color-logo-white.png",
                    x: undefined,
                    y: undefined,
                    height: 34,
                    width: 34,
                    excavate: true,
                  }}
                  className="hidden"
                />
              </div>

              <p className="text-sm text-white/60 mb-6">
                Scan to visit scanglobal.me/careers
              </p>

              <button
                onClick={handleDownloadQR}
                className="w-full inline-flex items-center justify-center gap-2 hover:bg-gold/90 transition text-sm font-medium text-black bg-gold border-gold/10 border rounded-full px-6 py-3"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Download High-Res QR Code
              </button>

              {/* <p className="text-xs text-white/45 mt-4">
                PNG format, 1200x1200px, optimized for print
              </p> */}
            </div>
          </div>
        </div>
      </section>

      {/* <footer className="relative z-20 border-white/10 border-t">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <p className="text-2xl font-semibold tracking-tight mb-2">
                Scanglobal
              </p>
              <p className="text-sm text-white/60 font-medium">
                Building exceptional teams since 2025
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-6 text-sm text-white/60">
              <a
                href="mailto:admin@scanglobal.me"
                className="hover:text-white/90 transition font-medium"
              >
                admin@scanglobal.me
              </a>
              <span className="w-px h-4 bg-white/20"></span>
              <a
                href="#"
                className="hover:text-white/90 transition font-medium"
              >
                Privacy Policy
              </a>
              <span className="w-px h-4 bg-white/20"></span>
              <p className="text-white/45 font-medium">
                © {new Date().getFullYear()} Scanglobal
              </p>
            </div>
          </div>
        </div>
      </footer> */}
    </>
  );
};

export default Careers;
